import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import useCartStore from "../../store/cartStore";
import { CONTACT, SITE_NAME } from "../../config/location";

const NAV = [
    { to: "/karte", label: "Speisekarte" },
    { to: "/ueber-uns", label: "Über uns" },
    { to: "/feiern", label: "Feiern & Catering" },
    { to: "/galerie", label: "Galerie" },
    { to: "/bestellen", label: "Bestellen" },
];

// Öffnungszeiten haben keine eigene Seite, sie stehen auf der Kontaktseite.
const HOURS_LINK = "/kontakt#oeffnungszeiten";

const MOBILE_NAV = [
    { to: "/", label: "Startseite" },
    { to: "/karte", label: "Speisekarte" },
    { to: "/ueber-uns", label: "Über uns" },
    { to: "/feiern", label: "Feiern & Catering" },
    { to: "/galerie", label: "Galerie" },
    { to: "/bestellen", label: "Bestellen" },
    { to: HOURS_LINK, label: "Öffnungszeiten" },
    { to: "/kontakt", label: "Kontakt" },
];

const navLinkClass = ({ isActive }) =>
    "no-underline" + (isActive ? " border-b border-forest" : "");

export default function SiteHeader() {
    const [open, setOpen] = useState(false);
    const [hidden, setHidden] = useState(false);
    const { key } = useLocation();
    const cartCount = useCartStore((state) => state.totalItems());
    const headerRef = useRef(null);
    const burgerRef = useRef(null);
    const overlayRef = useRef(null);
    const headerHidden = hidden && !open;

    // Header beim Runterscrollen ausblenden, beim Hochscrollen zeigen.
    useEffect(() => {
        let lastY = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            setHidden(y > 160 && y > lastY);
            lastY = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Klebende Leisten (.below-header) richten sich nach Höhe und Sichtbarkeit des Headers.
    useEffect(() => {
        const header = headerRef.current;
        if (!header) return undefined;
        const root = document.documentElement;
        const observer = new ResizeObserver(() => root.style.setProperty("--header-h", `${header.offsetHeight}px`));
        observer.observe(header);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const root = document.documentElement;
        root.toggleAttribute("data-header-hidden", headerHidden);
        return () => root.removeAttribute("data-header-hidden");
    }, [headerHidden]);

    // Bei jeder Navigation schließen, auch beim Tippen auf die aktuelle Seite (z. B. Startseite)
    useEffect(() => setOpen(false), [key]);

    // Burger-Menü: Escape schließt, Fokus bleibt im Menü, Seite scrollt nicht.
    useEffect(() => {
        if (!open) return undefined;
        const burger = burgerRef.current;
        const overlay = overlayRef.current;
        overlay?.querySelector("a")?.focus();
        document.body.style.overflow = "hidden";

        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
            if (e.key !== "Tab" || !overlay) return;
            const focusable = [burger, ...overlay.querySelectorAll("a")];
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            document.removeEventListener("keydown", onKey);
            burger?.focus();
        };
    }, [open]);

    return (
        <>
            <header
                ref={headerRef}
                className="fixed inset-x-0 top-0 z-20 grid grid-cols-[auto_1fr_auto] desk:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-3 desk:gap-x-[clamp(16px,2vw,32px)] px-5 py-2.5 desk:px-[var(--gutter)] desk:py-3.5 bg-creme/90 backdrop-blur-md border-b border-forest/10 font-figtree text-forest transition-transform duration-300"
                style={{ transform: headerHidden ? "translateY(-100%)" : "none" }}
            >
                <div className="flex items-center min-w-0">
                    <nav
                        aria-label="Hauptnavigation"
                        className="hidden nav:flex flex-wrap gap-x-[clamp(14px,2vw,28px)] xwide:gap-x-9 gap-y-2 text-xs xwide:text-[13px] font-medium tracking-[.08em] uppercase min-w-0"
                    >
                        {NAV.map((item) => (
                            <NavLink key={item.to} to={item.to} className={navLinkClass}>
                                {item.label}
                                {item.to === "/bestellen" && cartCount > 0 && (
                                    <span className="ml-1 text-messing-dunkel">({cartCount})</span>
                                )}
                            </NavLink>
                        ))}
                    </nav>
                    <button
                        ref={burgerRef}
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Menü schließen" : "Menü öffnen"}
                        aria-expanded={open}
                        aria-controls="mobile-menu"
                        className="nav:hidden -ml-2.5 flex h-11 w-11 flex-col items-center justify-center gap-[5px] bg-transparent"
                    >
                        <span
                            className="block h-[1.5px] w-[22px] bg-current transition-transform"
                            style={{ transform: open ? "translateY(3.25px) rotate(45deg)" : "none" }}
                        />
                        <span
                            className="block h-[1.5px] w-[22px] bg-current transition-transform"
                            style={{ transform: open ? "translateY(-3.25px) rotate(-45deg)" : "none" }}
                        />
                    </button>
                </div>

                {/* Mobil ohne Logo: Startseite ist über das Burger-Menü erreichbar. */}
                <Link to="/" className="hidden desk:block justify-self-center" aria-label={`${SITE_NAME} – Startseite`}>
                    <img
                        src="/media/logo-frei.png"
                        alt={SITE_NAME}
                        className="block h-[clamp(56px,7vw,76px)] xwide:h-[88px] w-auto"
                    />
                </Link>

                <div className="flex flex-wrap items-center justify-end gap-x-[clamp(14px,2vw,28px)] xwide:gap-x-9 gap-y-2 text-xs xwide:text-[13px] font-medium tracking-[.08em] uppercase min-w-0">
                    <Link to={HOURS_LINK} className="hidden nav:inline no-underline">
                        Öffnungszeiten
                    </Link>
                    <NavLink to="/kontakt" className={(s) => "hidden nav:inline " + navLinkClass(s)}>
                        Kontakt
                    </NavLink>
                    <Link
                        to="/reservierung"
                        className="no-underline whitespace-nowrap bg-forest !text-creme px-3.5 py-[11px] text-xs desk:px-5 desk:py-3 desk:text-xs xwide:px-6 xwide:py-3.5 xwide:text-[13px]"
                    >
                        Reservieren
                    </Link>
                </div>
            </header>

            {open && (
                <div
                    id="mobile-menu"
                    ref={overlayRef}
                    className="nav:hidden fixed inset-0 z-[19] flex flex-col justify-between overflow-y-auto bg-creme px-6 pt-[calc(var(--header-h,65px)+16px)] pb-8 font-figtree text-forest"
                >
                    <nav aria-label="Mobile Navigation" className="flex flex-col gap-1 font-caslon text-[clamp(20px,3.9svh,28px)] leading-[1.2]">
                        {MOBILE_NAV.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className="no-underline py-[clamp(3px,1svh,8px)] border-b border-forest/10"
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>
                    <div className="grid gap-3.5 mt-4">
                        <a href={CONTACT.phoneHref} className="no-underline text-[15px]">
                            {CONTACT.phone}
                        </a>
                        <Link to="/reservierung" className="btn btn-primary text-[13px] tracking-[.08em]">
                            Tisch reservieren
                        </Link>
                    </div>
                </div>
            )}
        </>
    );
}
